import React from 'react';
import { useState } from 'react';
import { Shield, FileText, Globe, Network, Rocket, TrendingUp, Building, ArrowRight, ShoppingBag, MessageSquare, Users, Code2, CheckCircle, Scale, Lock, BarChart, Award, Clock, Headphones } from 'lucide-react';
import { Link } from 'react-router-dom'; // Import Link for navigation
import { useTranslation } from 'react-i18next';

function LandingPage() {
const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      id: 1,
      question: "How does Kanadx deliver 40–60% cost savings without compromising quality?",
      answer: "Kanadx combines Canadian project management and governance with senior Indian engineering talent. This hybrid model reduces delivery costs while maintaining Canadian standards for quality, security, and accountability.",
      iconColor: "blue"
    },
    {
      id: 2,
      question: "Who manages my project—Canada or India?",
      answer: "Your project is managed by a Canada-based project manager who oversees delivery, communication, timelines, and quality. Global engineering teams execute under strict Canadian-led governance.",
      iconColor: "green"
    },
    {
      id: 3,
      question: "Is my data secure and PIPEDA-compliant?",
      answer: "Yes. Kanadx follows PIPEDA-aligned data protection practices, secure access controls, and continuous audits to ensure your data and IP remain protected under Canadian standards.",
      iconColor: "purple"
    },
    {
      id: 4,
      question: "What services can I scale with Kanadx?",
      answer: "You can scale Development, QA, Cloud, and AI teams based on your needs—without long-term hiring risk or overhead.",
      iconColor: "orange"
    },
    {
      id: 5,
      question: "How much visibility and control do I have over the team?",
      answer: "You have full transparency with sprint reviews, milestone reporting, direct communication, and performance tracking—just like an in-house team.",
      iconColor: "red"
    },
    {
      id: 6,
      question: "Is this better than hiring locally in Canada?",
      answer: "Yes. You gain Canadian accountability and leadership while avoiding high local hiring costs, long recruitment cycles, and scaling limitations—making growth faster and more cost-effective.",
      iconColor: "indigo"
    }
  ];

  const toggleFaq = (id) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section with Background Image */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <img
          src="/main.webp"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-gray-900/70"></div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 w-full">
          <div className="text-left max-w-5xl">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-3">
              Your Project. Protected by Canadian Law.
            </h1>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-8">
              Powered by Global Engineering.
            </h2>
            
            <p className="text-lg md:text-xl text-gray-200 mb-12 max-w-3xl leading-relaxed">
              Kanadx delivers offshore efficiency without offshore risk. Every client engages directly with a Canadian-registered IT services company — backed by secure, vetted global engineering teams.
            </p>
          </div>
        </div>
      </section>

      {/* Enterprise-Grade Data & IP Security */}
      <section className="py-10 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Enterprise-Grade Data & IP Security
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Complete transparency from contract to delivery
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 lg:gap-8 mb-8">
            <div className="flex flex-col items-center text-center w-full md:w-auto">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-2xl mb-4 shadow-lg hover:shadow-xl transition-all duration-300">
                <ShoppingBag className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Client</h3>
            </div>

            <div className="hidden md:block">
              <ArrowRight className="h-8 w-8 text-blue-400" />
            </div>
            <div className="md:hidden">
              <ArrowRight className="h-8 w-8 text-blue-400 transform rotate-90" />
            </div>

            <div className="flex flex-col items-center text-center w-full md:w-auto">
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-2xl mb-4 shadow-lg hover:shadow-xl transition-all duration-300">
                <FileText className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Kanadx PMO <br className="hidden sm:block" /> (Canada)</h3>
            </div>

            <div className="hidden md:block">
              <ArrowRight className="h-8 w-8 text-blue-400" />
            </div>
            <div className="md:hidden">
              <ArrowRight className="h-8 w-8 text-blue-400 transform rotate-90" />
            </div>

            <div className="flex flex-col items-center text-center w-full md:w-auto">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-2xl mb-4 shadow-lg hover:shadow-xl transition-all duration-300">
                <Network className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Global</h3>
            </div>

            <div className="hidden md:block">
              <ArrowRight className="h-8 w-8 text-blue-400" />
            </div>
            <div className="md:hidden">
              <ArrowRight className="h-8 w-8 text-blue-400 transform rotate-90" />
            </div>

            <div className="flex flex-col items-center text-center w-full md:w-auto">
              <div className="bg-gradient-to-br from-red-500 to-red-600 p-6 rounded-2xl mb-4 shadow-lg hover:shadow-xl transition-all duration-300">
                <Shield className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">QA & Audit</h3>
            </div>

            <div className="hidden md:block">
              <ArrowRight className="h-8 w-8 text-blue-400" />
            </div>
            <div className="md:hidden">
              <ArrowRight className="h-8 w-8 text-blue-400 transform rotate-90" />
            </div>

            <div className="flex flex-col items-center text-center w-full md:w-auto">
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-2xl mb-4 shadow-lg hover:shadow-xl transition-all duration-300">
                <ShoppingBag className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Client</h3>
            </div>
          </div>

          <div className="max-w-5xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 md:gap-16">
              <div>
                <ul className="space-y-5">
                  <li className="flex items-start">
                    <div className="mr-4 mt-1 flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                    <span className="text-gray-700 text-base">Canadian project managers & escalation path</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-4 mt-1 flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                    <span className="text-gray-700 text-base">Sprint reviews & milestone reporting</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-4 mt-1 flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                    <span className="text-gray-700 text-base">Continuous QA & code audits</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <ul className="space-y-5">
                  <li className="flex items-start">
                    <div className="mr-4 mt-1 flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                    <span className="text-gray-700 text-base">PIPEDA-aligned data protection</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-4 mt-1 flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                    <span className="text-gray-700 text-base">Secure access controls & encryption</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-4 mt-1 flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                    <span className="text-gray-700 text-base">Performance-based SLA enforcement</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* services */}
 <section className="relative py-12 md:py-20">
  {/* Background */}
  <div className="absolute inset-0 z-0">
    <div className="relative h-full w-full">
      <img
        src="/pic7.webp"
        alt="Services Background"
        className="object-cover w-full h-full blur-sm"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/40"></div>
    </div>
  </div>

  {/* Content */}
  <div className="relative z-10">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
      {/* Heading */}
      <div className="text-center mb-12 md:mb-16">
        <h2 className="text-[30px] font-bold text-white mb-4 md:mb-6 drop-shadow-lg">
          {t('homePage.services.title1')}{' '}
          <span className="text-red-500">
            {t('homePage.services.title2')}
          </span>
        </h2>
        <p className="text-[22px] text-gray-200 max-w-3xl mx-auto px-4 drop-shadow">
          {t('homePage.services.subtitle')}
        </p>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 px-4 md:px-0">
        {t('homePage.services.items', { returnObjects: true }).map((service, index) => (
          <div
            key={index}
            className="bg-white/95 backdrop-blur-md rounded-xl p-4 md:p-6 border border-white/30 hover:border-red-300 hover:shadow-2xl transition-all duration-300 group flex flex-col h-full shadow-xl"
          >
            <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4 flex-grow">
              <div
                className={`text-xl md:text-2xl ${service.color} p-2 md:p-3 rounded-lg group-hover:bg-red-50 transition-colors flex-shrink-0`}
              >
                {service.icon}
              </div>

              <div className="flex-1">
                <h4 className="font-bold text-gray-900 text-base md:text-lg mb-1 md:mb-2 group-hover:text-red-600 transition-colors">
                  {service.title}
                </h4>
                <p className="text-gray-700 text-sm">
                  {service.description}
                </p>
              </div>
            </div>

            <div className="mt-auto">
              <Link
                to={service.path}
                className="inline-flex items-center gap-1 text-sm text-red-600 font-medium hover:text-red-700 transition-colors"
              >
                {t('homePage.services.exploreService')}
                <svg
                  className="w-3 h-3 transform group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
</section>
{/* contarct  */}
      <section className="py-16 md:py-24 relative">
        <div className="absolute inset-0 z-0">
          <img 
            src="/l-1.jpg"  
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-grey-100 mb-4">
              One Contract. One Jurisdiction. One Point of Accountability.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <div className="group relative bg-gradient-to-bl from-blue-50 via-blue-100/50 to-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-blue-200">
              <div className="p-8 h-full flex flex-col">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="bg-gradient-to-tr from-blue-500 to-blue-700 p-4 rounded-2xl mb-6">
                    <FileText className="h-12 w-12 text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Canadian Contracts
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Contracts governed under Canadian law and jurisdiction
                  </p>
                </div>
              </div>
            </div>

            <div className="group relative bg-gradient-to-bl from-blue-50 via-blue-100/50 to-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-blue-200">
              <div className="p-8 h-full flex flex-col">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="bg-gradient-to-tr from-blue-500 to-blue-700 p-4 rounded-2xl mb-6">
                    <Shield className="h-12 w-12 text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Canadian Compliance
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Invoicing, billing, and tax compliance in Canada
                  </p>
                </div>
              </div>
            </div>

            <div className="group relative bg-gradient-to-bl from-blue-50 via-blue-100/50 to-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-blue-200">
              <div className="p-8 h-full flex flex-col">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="bg-gradient-to-tr from-blue-500 to-blue-700 p-4 rounded-2xl mb-6">
                    <Globe className="h-12 w-12 text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Kanadx Governance
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Global teams operate under our strict oversight & quality systems
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


 {/* Who This Is For Section - New Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black font-bold text-gray-900 mb-4">
              Who This Is For
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <div className="group relative bg-gradient-to-tl from-blue-100 via-blue-50/70 to-white/80 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="p-8 h-full flex flex-col">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="bg-blue-100 group-hover:bg-blue-600 transition-colors duration-300 p-4 rounded-2xl mb-6">
                    <Rocket className="h-12 w-12 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Startups & SMBs
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Fast delivery at predictable pricing under Canadian accountability.
                  </p>
                </div>
              </div>
            </div>

            <div className="group relative bg-gradient-to-tl from-blue-100 via-blue-50/70 to-white/80  bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="p-8 h-full flex flex-col">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="bg-green-100 group-hover:bg-green-600 transition-colors duration-300 p-4 rounded-2xl mb-6">
                    <TrendingUp className="h-12 w-12 text-green-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Growing Companies
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Scalable teams governed by Kanadx leadership and IP protection.
                  </p>
                </div>
              </div>
            </div>

            <div className="group relative bg-gradient-to-tl from-blue-100 via-blue-50/70 to-white/80  bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="p-8 h-full flex flex-col">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="bg-purple-100 group-hover:bg-purple-600 transition-colors duration-300 p-4 rounded-2xl mb-6">
                    <Building className="h-12 w-12 text-purple-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Enterprises
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Compliance-first delivery, SLA enforcement, and audit readiness.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


     

      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="/section.png"  
            alt="Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-gray-800/60"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6">
              Global Delivery, Canadian Accountability, Cost Efficiency.
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6 md:space-y-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4 mt-1">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-green-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-lg md:text-xl font-medium text-white">
                    40–60% cost savings compared to Canadian-only development
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4 mt-1">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-green-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-lg md:text-xl font-medium text-white">
                    No compromise on legal compliance, SLA, or quality.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4 mt-1">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-green-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-lg md:text-xl font-medium text-white">
                    Flexible engagement: hourly, monthly retainer, or enterprise pods
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="why-kanadx" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/bg.webp"
            alt="Background pattern representing global technology and connectivity"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gray-900/60"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              Why Companies Choose Kanadx Over Traditional Offshore Models
            </h2>
            <p className="text-lg text-gray-200 max-w-4xl mx-auto">
              Unlike conventional offshore vendors, Kanadx eliminates jurisdictional risk while preserving global cost efficiency. You work with a Canadian-registered IT services firm, under Canadian law, with full transparency, control, and enforceable SLAs—while our global engineering teams deliver at scale.
            </p>
          </div>

          <div className="w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="group bg-white/95 backdrop-blur-sm rounded-xl shadow-xl hover:shadow-2xl border border-white/20 transition-all duration-300 transform hover:-translate-y-2">
                <div className="p-6 h-full flex flex-col items-center text-center">
                  <div className="bg-blue-100 group-hover:bg-blue-600 transition-colors duration-300 p-4 rounded-xl mb-4">
                    <Scale className="h-8 w-8 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-3">Clear Legal Accountability</h4>
                  <p className="text-sm text-gray-600">
                    Direct legal responsibility under Canadian jurisdiction with enforceable contracts
                  </p>
                </div>
              </div>

              <div className="group bg-white/95 backdrop-blur-sm rounded-xl shadow-xl hover:shadow-2xl border border-white/20 transition-all duration-300 transform hover:-translate-y-2">
                <div className="p-6 h-full flex flex-col items-center text-center">
                  <div className="bg-green-100 group-hover:bg-green-600 transition-colors duration-300 p-4 rounded-xl mb-4">
                    <Lock className="h-8 w-8 text-green-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-3">Secure IP Ownership</h4>
                  <p className="text-sm text-gray-600">
                    Full intellectual property protection under Canadian law with clear ownership rights
                  </p>
                </div>
              </div>

              <div className="group bg-white/95 backdrop-blur-sm rounded-xl shadow-xl hover:shadow-2xl border border-white/20 transition-all duration-300 transform hover:-translate-y-2">
                <div className="p-6 h-full flex flex-col items-center text-center">
                  <div className="bg-purple-100 group-hover:bg-purple-600 transition-colors duration-300 p-4 rounded-xl mb-4">
                    <BarChart className="h-8 w-8 text-purple-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-3">Predictable Delivery Outcomes</h4>
                  <p className="text-sm text-gray-600">
                    Consistent, reliable project delivery with transparent milestones and SLAs
                  </p>
                </div>
              </div>

              <div className="group bg-white/95 backdrop-blur-sm rounded-xl shadow-xl hover:shadow-2xl border border-white/20 transition-all duration-300 transform hover:-translate-y-2">
                <div className="p-6 h-full flex flex-col items-center text-center">
                  <div className="bg-orange-100 group-hover:bg-orange-600 transition-colors duration-300 p-4 rounded-xl mb-4">
                    <Award className="h-8 w-8 text-orange-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-3">Enterprise-Ready Governance</h4>
                  <p className="text-sm text-gray-600">
                    Robust governance frameworks meeting enterprise compliance and security standards
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Compliance-First by Design */}
      <section id="compliance-first" className="py-16 md:py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Compliance-First by Design
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Kanadx is built for organizations that cannot afford shortcuts.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="group relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="p-6 lg:p-8 h-full flex flex-col">
                <div className="flex items-center mb-6">
                  <div className="bg-blue-100 group-hover:bg-blue-600 transition-colors duration-300 p-4 rounded-xl mr-4">
                    <svg className="h-8 w-8 text-blue-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Canadian Contracts & Invoicing</h3>
                </div>
                <p className="text-gray-600 flex-grow">
                  Contracts governed under Canadian law and jurisdiction with transparent Canadian invoicing and tax compliance.
                </p>
              </div>
            </div>

            <div className="group relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="p-6 lg:p-8 h-full flex flex-col">
                <div className="flex items-center mb-6">
                  <div className="bg-green-100 group-hover:bg-green-600 transition-colors duration-300 p-4 rounded-xl mr-4">
                    <svg className="h-8 w-8 text-green-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">IP Ownership Protection</h3>
                </div>
                <p className="text-gray-600 flex-grow">
                  Intellectual property ownership is clearly defined and protected under Canadian law, ensuring full rights retention.
                </p>
              </div>
            </div>

            <div className="group relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="p-6 lg:p-8 h-full flex flex-col">
                <div className="flex items-center mb-6">
                  <div className="bg-purple-100 group-hover:bg-purple-600 transition-colors duration-300 p-4 rounded-xl mr-4">
                    <svg className="h-8 w-8 text-purple-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Enterprise Security Standards</h3>
                </div>
                <p className="text-gray-600 flex-grow">
                  Data handling aligned with enterprise security standards, including encryption, access controls, and secure protocols.
                </p>
              </div>
            </div>

            <div className="group relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="p-6 lg:p-8 h-full flex flex-col">
                <div className="flex items-center mb-6">
                  <div className="bg-orange-100 group-hover:bg-orange-600 transition-colors duration-300 p-4 rounded-xl mr-4">
                    <svg className="h-8 w-8 text-orange-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Audit-Ready Documentation</h3>
                </div>
                <p className="text-gray-600 flex-grow">
                  Comprehensive documentation and reporting systems designed for regulatory audits and compliance verification.
                </p>
              </div>
            </div>

            <div className="group relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="p-6 lg:p-8 h-full flex flex-col">
                <div className="flex items-center mb-6">
                  <div className="bg-red-100 group-hover:bg-red-600 transition-colors duration-300 p-4 rounded-xl mr-4">
                    <svg className="h-8 w-8 text-red-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Performance-Linked SLA Enforcement</h3>
                </div>
                <p className="text-gray-600 flex-grow">
                  Enforceable Service Level Agreements with clear metrics, penalties, and performance guarantees for accountability.
                </p>
              </div>
            </div>

            <div className="group relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 md:col-span-2 lg:col-span-1">
              <div className="p-6 lg:p-8 h-full flex flex-col">
                <div className="flex items-center mb-6">
                  <div className="bg-indigo-100 group-hover:bg-indigo-600 transition-colors duration-300 p-4 rounded-xl mr-4">
                    <svg className="h-8 w-8 text-indigo-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Canadian Legal Framework</h3>
                </div>
                <p className="text-gray-600 flex-grow">
                  Full protection under Canadian legal system with enforceable contracts, dispute resolution, and jurisdictional clarity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Our Engagement Models - Modern Version */}
      <section id="engagement-models" className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/sec.jpg"
            alt="Technology team working collaboratively"
            className="w-full h-full object-cover scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-gray-800/60"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6">
              Choose Your Engagement Model
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Select the perfect collaboration framework that aligns with your project needs and business objectives.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="group bg-white/95 backdrop-blur-lg rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100/50 hover:border-blue-200 flex flex-col">
              <div className="p-10 flex flex-col flex-grow">
                <div className="mb-8">
                  <div className="bg-gradient-to-br from-blue-100 to-blue-50 group-hover:from-blue-500 group-hover:to-blue-400 transition-all duration-500 p-5 rounded-2xl inline-block mb-6">
                    <Users className="h-14 w-14 text-blue-600 group-hover:text-white transition-colors duration-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Dedicated Teams</h3>
                  <p className="text-gray-600 mb-8 leading-relaxed flex-grow">
                    Long-term, fully aligned engineering teams managed by Kanadx Canada, operating as an extension of your internal staff with full cultural integration.
                  </p>
                </div>
              </div>
            </div>

            <div className="group bg-white/95 backdrop-blur-lg rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100/50 hover:border-green-200 flex flex-col">
              <div className="p-10 flex flex-col flex-grow">
                <div className="mb-8">
                  <div className="bg-gradient-to-br from-green-100 to-emerald-50 group-hover:from-green-500 group-hover:to-emerald-400 transition-all duration-500 p-5 rounded-2xl inline-block mb-6">
                    <Clock className="h-14 w-14 text-green-600 group-hover:text-white transition-colors duration-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Fixed-Scope Projects</h3>
                  <p className="text-gray-600 mb-8 leading-relaxed flex-grow">
                    Well-defined milestones, locked timelines, and Canadian-governed contracts for predictable outcomes with guaranteed delivery dates.
                  </p>
                </div>
              </div>
            </div>

            <div className="group bg-white/95 backdrop-blur-lg rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100/50 hover:border-purple-200 flex flex-col">
              <div className="p-10 flex flex-col flex-grow">
                <div className="mb-8">
                  <div className="bg-gradient-to-br from-purple-100 to-violet-50 group-hover:from-purple-500 group-hover:to-violet-400 transition-all duration-500 p-5 rounded-2xl inline-block mb-6">
                    <Award className="h-14 w-14 text-purple-600 group-hover:text-white transition-colors duration-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Retainers & Enterprise Pods</h3>
                  <p className="text-gray-600 mb-8 leading-relaxed flex-grow">
                    Ongoing delivery with SLA-backed performance, ideal for scaling products and enterprise modernization with flexible resource allocation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>






      {/* NEW CTA Section - Added above FAQs */}
     <section className="relative py-16 md:py-24 overflow-hidden">
  {/* Background Image */}
  <img
    src="/cta.webp"   // put image inside public folder
    alt="Background"
    className="absolute inset-0 w-full h-full object-cover"
  />

  {/* Optional overlay for readability */}
  <div className="absolute inset-0 bg-blue-900/40"></div>

  {/* Content */}
  <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6">
      Let's Get Your Project Started!
    </h2>

    <p className="text-lg md:text-xl text-white mb-10 max-w-3xl mx-auto">
      Ready to experience Canadian accountability with global engineering efficiency?
      Contact us today to discuss your project.
    </p>

    <Link
      to="/contact"
      className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-red-600 rounded-xl shadow-lg hover:bg-red-700 transition"
    >
      Contact Us
      <ArrowRight className="ml-3 h-5 w-5" />
    </Link>
  </div>
</section>


      {/* FAQs Section */}
      <section id="faqs" className="py-16 md:py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Get answers to common questions about the Kanadx model
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className={`bg-white rounded-xl shadow-sm border border-gray-200 transition-all duration-300 overflow-hidden ${
                  openFaq === faq.id 
                    ? 'ring-2 ring-blue-500 shadow-lg' 
                    : 'hover:shadow-md hover:border-gray-300'
                }`}
              >
                <button
                  className="w-full px-6 py-5 text-left focus:outline-none"
                  onClick={() => toggleFaq(faq.id)}
                  aria-expanded={openFaq === faq.id}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <h3 className="text-lg md:text-xl font-semibold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                    </div>
                    <svg
                      className={`w-5 h-5 min-w-5 flex-shrink-0 transition-transform duration-300 ${
                        openFaq === faq.id 
                          ? `text-${faq.iconColor}-600 rotate-180` 
                          : 'text-gray-400'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openFaq === faq.id 
                      ? 'max-h-96 opacity-100' 
                      : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}

export default LandingPage;